import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  FileText,
  Trash2,
  Download,
  Upload as UploadIcon,
  Pin,
  PinOff,
} from "lucide-react";

const LOGO = "/bailey-logo.png";

const fmtSize = (bytes) => {
  const b = Number(bytes || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function DocumentsPage() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/documents");
      setDocs(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("description", description);
      await api.post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setTitle("");
      setDescription("");
      setFile(null);
      document.getElementById("doc-file-input").value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePin = async (d) => {
    try {
      await api.patch(`/documents/${d.id}`, { pinned: !d.pinned });
      toast.success(d.pinned ? "Unpinned" : "Pinned to top");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="documents-page">
      <header className="max-w-5xl mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={LOGO}
            alt="Bailey Family Reunion"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10"
          />
          <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
        </Link>
        <Link
          to="/"
          className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
          data-testid="documents-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="mb-10">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-white bg-[#1a2947] px-3 py-1 rounded-full inline-block mb-4">
            Denver · July 21-23, 2028
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            Family Documents
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl">
            Constitution, by-laws, meeting minutes, and any papers our reunion
            organizers want to keep on file for the family.
          </p>
        </div>

        {user && (
          <form
            onSubmit={upload}
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 mb-10"
            data-testid="documents-upload-form"
          >
            <div className="label-eyebrow mb-4">Upload a document</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Bailey Family Constitution"
                  className="mt-2 bg-stone-50 border-stone-200"
                  data-testid="documents-title-input"
                />
              </div>
              <div>
                <Label className="label-eyebrow">File</Label>
                <input
                  id="doc-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-2 block w-full text-sm text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-[#1a2947] file:text-white file:px-4 file:py-2 file:text-xs file:font-medium hover:file:bg-[#0f1a2e]"
                  data-testid="documents-file-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label className="label-eyebrow">Description (optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Adopted July 2016. Latest revision…"
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="documents-description-input"
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
                data-testid="documents-upload-btn"
              >
                <UploadIcon className="h-4 w-4 mr-1" />
                {busy ? "Uploading…" : "Upload"}
              </Button>
              <p className="text-xs text-stone-500">
                PDF, DOC/DOCX, TXT, RTF, or image · up to 25 MB
              </p>
            </div>
          </form>
        )}

        <div className="space-y-3" data-testid="documents-list">
          {docs.length === 0 ? (
            <div className="bg-white border border-[#e6e2d6] rounded-2xl p-14 text-center text-stone-500">
              No documents on file yet.
            </div>
          ) : (
            docs.map((d) => (
              <div
                key={d.id}
                className={
                  "relative bg-white border rounded-2xl p-5 flex items-center gap-4 " +
                  (d.pinned
                    ? "border-[#e7c96b] shadow-[0_8px_28px_-14px_rgba(215,180,60,0.5)]"
                    : "border-[#e6e2d6]")
                }
                data-testid={`document-row-${d.id}`}
              >
                {d.pinned && (
                  <div
                    className="absolute -top-2 left-6 bg-[#e7c96b] text-[#5a4310] text-[10px] tracking-[0.16em] uppercase font-semibold px-2 py-0.5 rounded-full"
                    data-testid={`document-pinned-ribbon-${d.id}`}
                  >
                    Pinned
                  </div>
                )}
                <div className="h-12 w-12 rounded-xl bg-[#faf7ef] border border-[#eae5d9] flex items-center justify-center text-[#1a2947]">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-stone-900 truncate">
                    {d.title || d.filename}
                  </div>
                  {d.description && (
                    <div className="text-sm text-stone-500 mt-0.5 truncate">
                      {d.description}
                    </div>
                  )}
                  <div className="text-xs text-stone-400 mt-1 tabular-nums">
                    {fmtDate(d.uploaded_at)} · {fmtSize(d.size)}
                  </div>
                </div>
                <a
                  href={`${API_BASE}/documents/file/${d.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border-2 border-[#1a2947] text-[#1a2947] font-semibold px-4 py-2 hover:bg-[#1a2947] hover:text-white transition-colors text-sm"
                  data-testid={`document-download-${d.id}`}
                >
                  <Download className="h-4 w-4" /> Open
                </a>
                {user && (
                  <>
                    <button
                      type="button"
                      onClick={() => togglePin(d)}
                      className={
                        "p-2 " +
                        (d.pinned
                          ? "text-[#8a7a3a] hover:text-[#6b5417]"
                          : "text-stone-400 hover:text-[#8a7a3a]")
                      }
                      title={d.pinned ? "Unpin" : "Pin to top"}
                      data-testid={`document-pin-${d.id}`}
                    >
                      {d.pinned ? (
                        <PinOff className="h-4 w-4" />
                      ) : (
                        <Pin className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(d)}
                      className="text-stone-400 hover:text-red-600 p-2"
                      title="Delete"
                      data-testid={`document-delete-${d.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
